import React from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
} from "@mui/material";
import StartOutlinedIcon from "@mui/icons-material/StartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons";
import { H7TitleLarge } from "../../../components/StyledComponents/Typography/Typography.tsx";
import OrgSiteGroupTree from "./OrgSiteGroupTree/OrgSiteGroupTree.js";
import { set } from "date-fns";
import { useGetOrgSiteLists } from "../../../api/queries/useGetOrgSiteLists.ts";
import AuradineErrorState from "../../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import AuradineDataNotFoundState from "../../../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

export const LeftSidePanel = React.memo((props) => {
  const {
    leftSidePanelOpen,
    setLeftSidePanelOpen,
    height,
    authToken,
  } = props;
  const [orgSiteGroups, setOrgSiteGroups] =
    React.useState();
  const { colors } = useTheme();
  const {
    data: orgSiteLists,
    isLoading: isOrgSiteGroupDataLoading,
    isFetching: isOrgSiteGroupDataFetching,
    isFetched: isOrgSiteGroupDataFetched,
    isRefetching: isOrgSiteGroupDataRefetching,
    refetch,
  } = useGetOrgSiteLists({
    authToken,
    leftPanelOpen: leftSidePanelOpen,
  });

  React.useEffect(() => {
    const params = new URLSearchParams(
      window.location.search
    );
    const hasSiteName = params.get("siteName");
    const hasDGName = params.get("dgname");
    const hasOrgName = params.get("org_name");

    if (hasSiteName || hasDGName || hasOrgName) {
      setLeftSidePanelOpen(true);
    } else {
      const siteGroupCS =
        JSON.parse(sessionStorage.getItem("siteGroupCS")) ||
        {};
      if (
        siteGroupCS.siteName ||
        siteGroupCS.dgname ||
        siteGroupCS.org_name
      ) {
        setLeftSidePanelOpen(true);
      }
    }
  }, []);

  React.useEffect(() => {
    if (orgSiteLists) {
      if (
        orgSiteLists?.status === "ok" &&
        orgSiteLists?.orgList?.length > 0
      ) {
        const orgList = orgSiteLists?.orgList;
        setOrgSiteGroups(orgList);
      }
    }
  }, [orgSiteLists, isOrgSiteGroupDataFetching]);

  console.log("orgSitegroups", orgSiteGroups);

  const onorgSiteGroupSearch = (e) => {
    if (e.target.value === "") {
      setOrgSiteGroups(orgSiteLists?.orgList);
      return;
    } else {
      const orgSitesData = [...orgSiteGroups];
      const found = orgSitesData.filter((org) => {
        return (
          (org &&
            org.org_name &&
            org.org_name
              .toLowerCase()
              .includes(e.target.value.toLowerCase())) ||
          (org.siteList &&
            org.siteList.some(
              (site) =>
                (site.siteName &&
                  site.siteName
                    .toLowerCase()
                    .includes(
                      e.target.value.toLowerCase()
                    )) ||
                (site.groups &&
                  site.groups.some(
                    (group) =>
                      group.DGName &&
                      group.DGName.toLowerCase().includes(
                        e.target.value.toLowerCase()
                      )
                  ))
            ))
        );
      });
      setOrgSiteGroups(found);
    }
  };
  const [searchValue, setSearchValue] = React.useState("");

  return (
    <Box
      className={"left-side-panel"}
      display={"flex"}
      flexDirection={"column"}
      gap={4}
      height={height}
      paddingLeft={"0.75rem"}
      paddingRight={"0.75rem"}
      minWidth={leftSidePanelOpen ? "17vw" : "3vw"}
      width={leftSidePanelOpen ? "17vw" : "3vw"}
      flexGrow={leftSidePanelOpen ? 0.1 : 0}
      flexShrink={0}
      borderRight={`1px solid ${colors?.border?.card}`}
      sx={{
        transition: "all 0.3s ease-in-out",
      }}
    >
      <Box
        className={"control-button"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={
          leftSidePanelOpen ? "space-between" : "center"
        }
      >
        {leftSidePanelOpen && (
          <H7TitleLarge>Orgs & Sites</H7TitleLarge>
        )}
        <Tooltip title="Orgs & Sites">
          <span>
            <StyledIconButton 
              colors={colors}
              onClick={() => {
                setLeftSidePanelOpen((prev) => !prev);
              }}
            >
              {leftSidePanelOpen ? (
                <span
                  style={{
                    height: "24px",
                    width: "24px",
                  }}
                  className="material-symbols-outlined"
                >
                  right_panel_open
                </span>
              ) : (
                <span
                  style={{
                    height: "24px",
                    width: "24px",
                  }}
                  className="material-symbols-outlined"
                >
                  right_panel_close
                </span>
              )}
            </StyledIconButton>
          </span>
        </Tooltip>
      </Box>
      {leftSidePanelOpen && (
        <>
          <Collapse
            in={leftSidePanelOpen}
            collapsedSize={0}
          >
            {orgSiteLists &&
              orgSiteLists.status === "ok" &&
              Array.isArray(orgSiteLists.orgList) &&
              orgSiteLists.orgList.length > 0 && (
                <Paper
                  component="form"
                  elevation={0}
                  sx={{
                    p: "2px 4px",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    backgroundColor: colors?.background?.secondary,  
                    borderRadius: "1.75rem",
                    paddingLeft: "1rem",
                  }}
                >
                  <Tooltip
                    title={searchValue}
                    disableFocusListener
                    disableTouchListener
                  >
                    <InputBase
                      sx={{ flex: 1, "& .MuiInputBase-input": {
                        color: colors?.text?.primary,             // normal text
                        "::placeholder": {
                          color: colors?.text?.hint,  
                        },
                      }}}
                      placeholder="Search for an org, site, or group"
                      onChange={(event) => {
                        setSearchValue(event.target.value);
                        onorgSiteGroupSearch(event);
                      }}
                      // multiline
                      // maxRows={4}
                      inputProps={{
                        "aria-label": "search google maps",
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Tooltip>
                  <IconButton
                    type="button"
                    sx={{ p: "10px", color: colors?.text?.hint }}
                    aria-label="search"
                  >
                    <SearchIcon />
                  </IconButton>
                </Paper>
              )}
            {(isOrgSiteGroupDataLoading ||
              isOrgSiteGroupDataFetching ||
              isOrgSiteGroupDataRefetching) && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                flexGrow={1}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <CircularProgress
                  size={"1.5rem"}
                  sx={{
                    color: colors?.primary?.main,  
                  }}
                />
              </Box>
            )}

            {isOrgSiteGroupDataFetched &&
              orgSiteLists?.status === "ok" &&
              orgSiteLists &&
              orgSiteLists?.orgList?.length > 0 && (
                <>
                  <OrgSiteGroupTree
                    orgSiteGroups={orgSiteGroups}
                    onorgSiteGroupSearch={
                      onorgSiteGroupSearch
                    }
                    isOrgSiteGroupDataFetched={
                      isOrgSiteGroupDataFetched
                    }
                    sidePanelMode={true}
                  />
                </>
              )}
            {isOrgSiteGroupDataFetched &&
              orgSiteLists?.status === "ok" &&
              orgSiteLists?.orgList?.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                >
                  <AuradineDataNotFoundState
                    message={`You may not have permissions to any sites. Please contact superadmin`}
                    fontSize="1rem"
                  />
                </Box>
              )}
            {isOrgSiteGroupDataFetched &&
              orgSiteLists?.status !== "ok" && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                >
                  <AuradineErrorState
                    message={`${orgSiteLists?.msg}. Please contact your superadmin.`}
                    fontSize="1rem"
                  />
                </Box>
              )}
          </Collapse>
        </>
      )}
    </Box>
  );
});
